import { Image } from 'react-datocms';
import { Alert } from '@zopauk/web-ui';
import { Post } from '@/components/Post';
import TealWave from '@/components/TealWave';
import { Pagination } from '@/components/Pagination';
import { Placeholder } from '@/components/Placeholder';
import { MarkdownToJSX } from '@/components/MarkdownToJSX';
import type { AuthorContext } from '@/utils/models/author';
import { type AuthorForPostListFragment, type PostsForAuthorFragment, useAuthorQuery } from '@/utils/dato/generated';

const LinkedInIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 40 40"
    aria-hidden="true"
    focusable="false"
    height="40"
    fill="none"
    width="40"
  >
    <rect fill="#0E2C27" height="40" width="40" rx="17" />
    <path
      d="M14.5 17H11.5V29H14.5V17ZM13 15.5C13.8284 15.5 14.5 14.8284 14.5 14C14.5 13.1716 13.8284 12.5 13 12.5C12.1716 12.5 11.5 13.1716 11.5 14C11.5 14.8284 12.1716 15.5 13 15.5ZM28.5 29H25.5V23.25C25.5 20.75 22.5 20.9375 22.5 23.25V29H19.5V17H22.5V18.7625C23.7969 16.4375 28.5 16.2656 28.5 21.1563V29Z"
      fill="white"
    />
  </svg>
);

const PostsForAuthor = ({ pageContext }: { pageContext: AuthorContext } & PostsForAuthorFragment) => {
  const { error, data } = useAuthorQuery({
    perPage: pageContext.pagination.perPage,
    skip: pageContext.pagination.skip,
    slug: pageContext.slug,
  });

  if (data?.author) {
    const { author } = data;

    return (
      <div className="flex flex-col">
        <div className="bg-quarterary w-full overflow-hidden">
          <div className="max-w-[800px] mx-auto px-7 pt-7 pb-5 md:pt-[76px] md:pb-[46px]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 max-w-4xl">
              {author.avatar?.profileImage ? (
                <div className="w-24 h-24 md:w-[172px] md:h-[172px] shrink-0 order-first md:order-last md:ml-auto">
                  <Image style={{ height: '100%', width: '100%' }} data={author.avatar.profileImage} />
                </div>
              ) : null}
              <div className="flex flex-col items-start">
                <h1 className="text-[40px] font-serif font-semibold leading-[57px]">
                  {author.name || 'Unknown author'}
                </h1>
                {author.jobTitle ? (
                  <p className="text-base text-base-content/70 font-medium mt-6">{author.jobTitle}</p>
                ) : null}
                {author.bio ? (
                  <div className="text-base text-base-content leading-relaxed mt-8">
                    <MarkdownToJSX md={author.bio} />
                  </div>
                ) : null}
                {author.linkedinUrl ? (
                  <a
                    aria-label={`${author.name} on LinkedIn`}
                    href={author.linkedinUrl}
                    rel="noopener noreferrer"
                    className="mt-[46px]"
                    target="_blank"
                  >
                    <LinkedInIcon />
                  </a>
                ) : null}
              </div>
            </div>
            {pageContext.pagination.totalPages > 1 ? (
              <p className="text-sm mt-6 text-base-content/60">
                Page {pageContext.pagination.currentPage} of {pageContext.pagination.totalPages}
              </p>
            ) : null}
          </div>
          <TealWave />
        </div>

        <div className="mt-8 mb-8 lg:mb-11 max-w-7xl mx-auto w-full">
          <div className="px-5 mx-auto max-w-[825px]">
            {!author.posts?.length ? (
              <Alert variant="info">{author.name} doesn't have any articles to display.</Alert>
            ) : null}

            {author.posts.map((post) => (
              <Post
                image={
                  post?.featuredImage?.responsiveImage ? (
                    <Image
                      data={post.featuredImage.responsiveImage}
                      style={{ margin: '0 auto' }}
                      objectPosition="50% 50%"
                    />
                  ) : undefined
                }
                author={post.author as AuthorForPostListFragment}
                categories={post.categories}
                excerpt={post.excerpt}
                title={post.title}
                date={post.date}
                slug={post.slug}
                key={post.id}
              />
            ))}
          </div>

          <Pagination basePath={`/blog/author/${pageContext.slug}`} {...pageContext.pagination} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative mt-10 mb-8 lg:mb-11 flex flex-col max-w-7xl mx-auto w-full px-4">
        <Alert variant="warning">We've been unable to load this Author</Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="bg-quarterary w-full overflow-hidden">
        <div className="max-w-[800px] mx-auto px-7 pt-7 pb-5 md:pt-[76px] md:pb-[46px]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 max-w-4xl">
            <div className="shrink-0 order-first md:order-last md:ml-auto">
              <Placeholder className="w-24 h-24 md:w-[172px] md:h-[172px]" />
            </div>
            <div className="flex flex-col items-start w-full">
              <Placeholder className="w-[280px] h-[46px]" />
              <Placeholder className="w-[180px] h-[20px]" />
              <Placeholder className="w-full h-[72px]" />
            </div>
          </div>
        </div>
        <TealWave />
      </div>

      <div className="mt-8 mb-8 lg:mb-11 max-w-7xl mx-auto w-full">
        <div className="px-5 mx-auto max-w-[825px]">
          <Placeholder className="h-[182px]" />
          <Placeholder className="h-[182px]" />
          <Placeholder className="h-[182px]" />
          <Placeholder className="h-[182px]" />
          <Placeholder className="mx-auto w-[180px] h-[52px]" />
        </div>
      </div>
    </div>
  );
};

export default PostsForAuthor;
